import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';

import { Locale } from '../../../i18n';
import { parseDateWithoutTime } from '../../helpers/parse-date';
import { textifyDeclaredUnit } from '../../helpers/textify-numerical-footprint-values-with-their-units';
import { EmissionSymbol } from '../../interfaces/IFootprint';
import CrossSectoralStandards from '../cross-sectoral-standards/CrossSectoralStandards';
import EmissionValueWithSuffix from '../emission-value-with-suffix/EmissionValueWithSuffix';
import { ActivePcfViewModel } from './data-utils';

type ActivePcfDetailsProps = {
  data: ActivePcfViewModel;
  isLoading: boolean;
};

const ActivePcfDetails = ({ data, isLoading }: ActivePcfDetailsProps): JSX.Element => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Function to get reference period text and icon as JSX.Element
  const getReferencePeriod = (): JSX.Element => {
    return (
      <div className='value-text-faded'>
        {data?.referencePeriodStart && data?.referencePeriodEnd ? (
          <div className='flex flex-row md:gap-2 gap-1'>
            <i className='pi pi-calendar' />

            {`${parseDateWithoutTime(
              data?.referencePeriodStart,
              language as Locale,
            )} - ${parseDateWithoutTime(data?.referencePeriodEnd, language as Locale)}`}
          </div>
        ) : (
          t('global.notSpecified')
        )}
      </div>
    );
  };

  return (
    <div className='flex flex-column gap-4'>
      <h4 className='-mb-1'>{t('activePcfSection.title')}</h4>

      <div className='grid grid-nogutter gap-4 md:gap-0'>
        {/* PCF ID */}
        <div className='col-12 md:col-6 md:pr-4 flex flex-column md:gap-2 gap-1'>
          <span className='font-medium'>{t('activePcfSection.pcfId')}</span>
          {isLoading ? (
            <Skeleton height='1rem' className='pt-4 w-full' />
          ) : (
            <div className='value-text-faded'>{data?.id ?? t('global.notSpecified')}</div>
          )}
        </div>

        {/* Date Created */}
        <div className='col-12 md:col-3 md:pr-4 flex flex-column md:gap-2 gap-1'>
          <span className='font-medium'>{t('activePcfSection.dateCreated')}</span>

          {isLoading ? (
            <Skeleton height='1rem' className='pt-4 w-full' />
          ) : (
            <div className='value-text-faded'>
              {parseDateWithoutTime(data?.createdDate, language as Locale)}
            </div>
          )}
        </div>

        {/* Declared Unit */}
        <div className='col-12 md:col-3 flex flex-column md:gap-2 gap-1'>
          <span className='font-medium'>{t('activePcfSection.declaredUnit')}</span>

          {isLoading ? (
            <Skeleton height='1rem' className='pt-4 w-full' />
          ) : (
            <div className='value-text-faded'>
              {data?.unitaryProductAmount != null && data?.declaredUnit != null
                ? data?.unitaryProductAmount + ' ' + textifyDeclaredUnit(data?.declaredUnit)
                : t('global.notSpecified')}
            </div>
          )}
        </div>
      </div>

      <div className='grid grid-nogutter gap-4 md:gap-0'>
        {/* Cross-sectoral Standard */}
        <div className='col-12 md:col-6 md:pr-4 flex flex-column md:gap-2 gap-1'>
          <span className='font-medium'>{t('activePcfSection.crossSectoralStandard')}</span>
          {isLoading ? (
            <Skeleton height='1rem' className='pt-4 w-full' />
          ) : (
            <CrossSectoralStandards standards={data?.crossSectoralStandards ?? []} />
          )}
        </div>

        {/* Reference Period */}
        <div className='col-12 md:col-6 flex flex-column md:gap-2 gap-1'>
          <span className='font-medium'>{t('activePcfSection.referencePeriod')}</span>
          {isLoading ? <Skeleton height='1rem' className='pt-4 w-full' /> : getReferencePeriod()}
        </div>
      </div>

      <div className='grid grid-nogutter gap-4 md:gap-0'>
        {/* PCF excl. */}
        <div className='col-12 md:col-6 md:pr-4 flex flex-column md:gap-2 gap-1 text-primary'>
          <span className='font-medium'>
            {t('activePcfSection.pcfExcludingBiogenicEmissionsAndRemovals')}
          </span>
          {isLoading ? (
            <Skeleton height='1rem' className='pt-4 w-full' />
          ) : (
            <EmissionValueWithSuffix
              emissionValue={data?.pcfExcludingBiogenic}
              emissionSymbol={EmissionSymbol.KG_CO2E}
              declaredUnit={data?.declaredUnit}
              abbreviateUnit
              unitaryProductAmount={data?.unitaryProductAmount}
              suffixTextClassName='text-primary'
              numericalValueTextClassName='font-bold text-primary'
            />
          )}
        </div>

        {/* PCF incl. */}
        <div className='col-12 md:col-6 flex flex-column md:gap-2 gap-1'>
          <span className='font-medium'>
            {t('activePcfSection.pcfIncludingBiogenicEmissionsAndRemovals')}
          </span>
          {isLoading ? (
            <Skeleton height='1rem' className='pt-4 w-full' />
          ) : (
            <EmissionValueWithSuffix
              emissionValue={data?.pcfIncludingBiogenic}
              emissionSymbol={EmissionSymbol.KG_CO2E}
              declaredUnit={data?.declaredUnit}
              abbreviateUnit
              unitaryProductAmount={data?.unitaryProductAmount}
              suffixTextClassName='text-black-alpha-60'
              numericalValueTextClassName='font-bold text-black-alpha-60'
            />
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className='flex flex-column md:gap-2 gap-1'>
        <span className='font-medium'>{t('activePcfSection.disclaimer')}</span>

        {isLoading ? (
          <Skeleton height='4rem' className='pt-4 w-full' />
        ) : (
          <div className='value-text-faded'>
            {data.pcfLegalStatement ?? t('global.notSpecified')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivePcfDetails;
